/**
 * Data filtering helper functions
 * Handles filtering and sorting of tags and client/project data
 */

/**
 * Filter and sort tag items based on search query
 * @param {Array} tagOptions - Array of tag options
 * @param {string} tagQuery - Search query string
 * @returns {Array} Filtered and sorted array of tags
 */
export function filterTagItems(tagOptions, tagQuery) {
  try {
    const seen = new Set();
    return tagOptions
      .filter((item) => {
        try {
          if (!item.tag_name) return false;
          if (seen.has(item.tag_name.toLowerCase())) return false;
          seen.add(item.tag_name.toLowerCase());
          return item.tag_name.toLowerCase().includes(tagQuery.toLowerCase());
        } catch (innerError) {
          console.error('Error filtering tag:', innerError);
          return false; // Skip this item if error occurs
        }
      })
      .sort((a, b) => {
        try {
          return a.tag_name.localeCompare(b.tag_name);
        } catch (innerError) {
          console.error('Error sorting tags:', innerError);
          return 0; // Keep original order if sorting fails
        }
      });
  } catch (error) {
    console.error('Error processing tagOptions:', error);
    return []; // Fallback to empty array
  }
}

/**
 * Filter and sort client data based on project filter and status
 * @param {Array} clientData - Array of client data with projects
 * @param {string} filter - Project name filter string
 * @param {string} projectStatusFilter - Status filter ("All", "Active", "Inactive")
 * @returns {Array} Filtered and sorted array of clients
 */
export function filterClientData(clientData, filter, projectStatusFilter) {
  try {
    return clientData
      // IMPORTANT: do not clone client objects here.
      // `selectedClients` in the UI is a Set of object references, so cloning breaks `.has()`.
      .filter((client) => {
        try {
          const hasAnyMatchingProject = (client?.projects || []).some((project) => {
            try {
              // Exclude archived projects
              if (project?.is_archive === true) return false;

              const nameMatches = project?.project_name
                ?.toLowerCase()
                .includes(filter.toLowerCase());

              const statusMatches =
                projectStatusFilter === "All"
                  ? true
                  : projectStatusFilter === "Active"
                    ? project?.is_active === true
                    : project?.is_active === false;

              return Boolean(nameMatches && statusMatches);
            } catch (innerError) {
              console.error("Error filtering project:", innerError);
              return false;
            }
          });

          return hasAnyMatchingProject;
        } catch (clientError) {
          console.error("Error filtering client:", clientError);
          return false;
        }
      })
      .sort((a, b) => {
        try {
          return a.client_name.localeCompare(b.client_name);
        } catch (sortError) {
          console.error('Error sorting clients:', sortError);
          return 0; // Keep original order if sorting fails
        }
      });
  } catch (error) {
    console.error('Error processing clientData:', error);
    return []; // Fallback to empty array
  }
}
